/**
 * Help Center Index Page
 * Main hub for all documentation and guides
 */

import { Link } from 'react-router-dom';
import {
  Rocket,
  Calendar,
  GitBranch,
  Users,
  Settings,
  Code,
  ArrowRight,
  BookOpen,
  Phone,
  Mail,
} from 'lucide-react';
import { pageStyles, cardStyles, buttonStyles } from '../../lib/styles';

interface QuickLinkProps {
  to: string;
  icon: React.ElementType;
  title: string;
  description: string;
}

function QuickLink({ to, icon: Icon, title, description }: QuickLinkProps) {
  return (
    <Link
      to={to}
      className={`${cardStyles.container} group hover:border-blue-300 hover:shadow-sm transition-all`}
    >
      <div className="flex items-start gap-3 p-3">
        <div className="flex-shrink-0 p-2 rounded-md bg-blue-50 text-blue-600 group-hover:bg-blue-100">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
            {title}
          </h3>
          <p className="text-[11px] text-gray-500 mt-1">{description}</p>
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-0.5" />
      </div>
    </Link>
  );
}

export function HelpCenter() {
  return (
    <div className={pageStyles.container}>
      {/* Welcome Section */}
      <div className={cardStyles.container}>
        <div className={cardStyles.content}>
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-full bg-blue-100">
              <BookOpen className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Bienvenido al Centro de Ayuda
              </h2>
              <p className="text-xs text-gray-600 mt-1.5">
                Aqui encontraras guias completas para aprovechar al maximo la
                plataforma Leal CRM. Desde crear tu primera cita hasta
                configurar el sistema como administrador.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links Grid */}
      <div>
        <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
          Guias Disponibles
        </h2>
        <div className="grid gap-3 md:grid-cols-2">
          <QuickLink
            to="/help/getting-started"
            icon={Rocket}
            title="Primeros Pasos"
            description="Conoce la plataforma y empieza a trabajar rapidamente"
          />
          <QuickLink
            to="/help/booking-flow"
            icon={Calendar}
            title="Flujo de Citas"
            description="Aprende a crear y gestionar citas de clientes"
          />
          <QuickLink
            to="/help/job-tracking"
            icon={GitBranch}
            title="Seguimiento de Trabajos"
            description="Entiende los estados y como avanzar los trabajos"
          />
          <QuickLink
            to="/help/customer-portal"
            icon={Users}
            title="Portal del Cliente"
            description="Funciones de seguimiento para tus clientes"
          />
          <QuickLink
            to="/help/admin-settings"
            icon={Settings}
            title="Configuracion Admin"
            description="Configura servicios, bahias y reglas del sistema"
          />
          <QuickLink
            to="/help/embed-widget"
            icon={Code}
            title="Widget de Reservas"
            description="Integra el formulario de citas en tu pagina web"
          />
        </div>
      </div>

      {/* Contact Support */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Necesitas mas ayuda?</span>
        </div>
        <div className={cardStyles.content}>
          <p className="text-xs text-gray-600 mb-3">
            Si no encuentras la respuesta que buscas, contacta con nuestro
            equipo de soporte.
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href="tel:+34123456789"
              className={buttonStyles.secondary}
            >
              <Phone className="h-3.5 w-3.5" />
              <span className="text-xs">+34 123 456 789</span>
            </a>
            <a
              href="mailto:soporte@lealcrm.com"
              className={buttonStyles.secondary}
            >
              <Mail className="h-3.5 w-3.5" />
              <span className="text-xs">soporte@lealcrm.com</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HelpCenter;
